/*
Remarquables screen — 2-level catalogue (parity Android v1.1.0+).

Top-level toggle: Catalogue | Historique.
- Catalogue : 183 remarquables groupés par arrondissement avec headers
              sticky « 19e arrondissement · 10/12 » (parité CatalogueView),
              tri intra-section genre/espèce/id. Capturés = card photo +
              identité, cliquable ; non-capturés = silhouette « ??? »
              (parity LockedRemarquableCard). Pas d'empty state ici : la
              liste des ??? reste visible même à zéro capture (Android).
- Historique : « Découverts (N/183) », seuls les capturés, tri
              lastCaptureTs décroissant, « Capture : jj/mm/aaaa ».
              Empty state si zéro capture.

L'arrondissement (group-by) et l'adresse complète des cards découvertes
viennent du sidecar arbre-attrs.json (v3+) ; fallback arr court si
l'adresse manque.

State :
- sessionStorage 'remarquables.tab' → 'catalogue' | 'historique' (survit
  aux back-nav, alignement Arboretum).
*/

import { navigate, kPaths } from '../router'
import {
	ensureSpeciesIndex,
	speciesNameFor,
	displayName,
	type SpeciesName,
} from '../data/speciesIndex'
import { ensureArbresFeatures, type ArbreFeature } from '../data/arbresFeatures'
import { ensureRemarquablesInfo, type RemarquableEntry } from '../data/remarquablesInfo'
import { ensureArbreAttrs, arrFor, attrsFor } from '../data/arbreAttrs'
import { arrKeyHeaderLabel, arrKeyLabel, type ArrKey } from '../data/arrKey'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { mountTabSelector, type TabSelectorController } from './components/TabSelector'
import { renderEmptyState } from './components/EmptyState'
import { renderSkeletonBlock, renderSkeletonListCard } from './components/Skeleton'

type RemarquablesTab = 'catalogue' | 'historique'
const kTabStorageKey = 'remarquables.tab'

// Date courte « 14/05/2026 » — parité Android DateFormat.SHORT.
const kFrenchDateShort = new Intl.DateTimeFormat('fr-FR')

interface CardRow
{
	arbreId: number
	speciesIndex: number
	speciesName: SpeciesName | null
	info: RemarquableEntry
	captured: boolean
	lastCaptureTs: number  // 0 when not captured — used by the Historique sort
	photoFilename: string | null  // most-recent capture photo — thumb des cards découvertes
	arr: ArrKey | null  // arrondissement via arbre-attrs — group-by Catalogue
	adresse: string | null  // adresse complète (sidecar v3) — ligne d'adresse des cards découvertes
}

interface ViewModel
{
	rows: CardRow[]
	capturedCount: number
	totalCount: number
	loadError: string | null
}

let fTabCtrl: TabSelectorController | null = null  // mounted level-1 selector, destroyed on remount
let fActiveObjectUrls: string[] = []  // thumbnails blob URLs du mount courant, révoquées au remount

/*
Mount the catalogue screen. Renders a static shell synchronously, then
hydrates async — same pattern as the other list screens.
*/
export function mountRemarquablesScreen(inRoot: HTMLElement): void
{
	if (fTabCtrl) { fTabCtrl.destroy(); fTabCtrl = null }
	revokeObjectUrls()
	inRoot.innerHTML = `
		<div class="screen arboretum">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Remarquables</h1>
			</header>
			<div class="arboretum-subheader" id="rmq-subheader">
				${renderSkeletonBlock({ width: '180px', height: '14px' })}
			</div>
			<div class="arboretum-tabs" id="rmq-tab1"></div>
			<div class="arboretum-content" id="rmq-content">
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
				${renderSkeletonListCard()}
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => navigate(kPaths.map()))
	void hydrate(inRoot)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function readTab(): RemarquablesTab
{
	const vRaw = sessionStorage.getItem(kTabStorageKey)
	return vRaw === 'historique' ? 'historique' : 'catalogue'
}

function writeTab(inTab: RemarquablesTab): void
{
	try { sessionStorage.setItem(kTabStorageKey, inTab) } catch { /* private mode quota */ }
}

async function hydrate(inRoot: HTMLElement): Promise<void>
{
	const vSubheader = inRoot.querySelector<HTMLElement>('#rmq-subheader')
	const vTabHost = inRoot.querySelector<HTMLElement>('#rmq-tab1')
	const vContent = inRoot.querySelector<HTMLElement>('#rmq-content')
	if (!vSubheader || !vTabHost || !vContent) return

	const vModel = await loadViewModel()

	if (vModel.loadError)
	{
		vSubheader.innerHTML = `<span class="arboretum-error">⚠ ${escapeHtml(vModel.loadError)}</span>`
		vContent.innerHTML = ''
		return
	}

	// HeaderCard primaryContainer — parité RemarquablesScreen.kt.
	vSubheader.innerHTML = `
		<div class="arboretum-header-card">
			<div class="arboretum-header-card-title display">${vModel.capturedCount} / ${vModel.totalCount} remarquables découverts</div>
		</div>
	`

	let vActiveTab = readTab()
	fTabCtrl = mountTabSelector({
		host: vTabHost,
		options: [
			{ id: 'catalogue', label: 'Catalogue' },
			{ id: 'historique', label: 'Historique' },
		],
		activeId: vActiveTab,
		onChange: (inId) => {
			vActiveTab = (inId === 'historique' ? 'historique' : 'catalogue')
			writeTab(vActiveTab)
			renderActiveTab(vContent, vModel, vActiveTab)
		},
	})

	renderActiveTab(vContent, vModel, vActiveTab)
}

async function loadViewModel(): Promise<ViewModel>
{
	try
	{
		const [vFeatures, vInfo, , vAllCaptures] = await Promise.all([
			ensureArbresFeatures(),
			ensureRemarquablesInfo(),
			ensureSpeciesIndex(),
			listCaptures(),
			ensureArbreAttrs(),  // arrFor() — group-by arrondissement du Catalogue
		])

		// Maps arbreId -> most-recent capture ts + photo, pour le tri
		// Historique et les thumbnails (parité firstPhotoByArbreId).
		const vLastTsByArbre = new Map<number, number>()
		const vPhotoByArbre = new Map<number, string>()
		for (const vCap of vAllCaptures)
		{
			const vPrev = vLastTsByArbre.get(vCap.arbreId)  // ms timestamp; max wins
			if (vPrev === undefined || vCap.timestamp > vPrev)
			{
				vLastTsByArbre.set(vCap.arbreId, vCap.timestamp)
				if (vCap.photoFilename) vPhotoByArbre.set(vCap.arbreId, vCap.photoFilename)
			}
		}

		const vRemarquableFeatures = vFeatures.filter((inF) => inF.remarquable)
		const vById = new Map<number, ArbreFeature>()
		for (const vF of vRemarquableFeatures) vById.set(vF.id, vF)

		const vRows: CardRow[] = []
		for (const vEntry of vInfo.values())
		{
			const vFeature = vById.get(vEntry.id)  // may be missing if remarquables-info has stale ids
			if (!vFeature) continue
			vRows.push({
				arbreId: vEntry.id,
				speciesIndex: vFeature.speciesIndex,
				speciesName: speciesNameFor(vFeature.speciesIndex),
				info: vEntry,
				captured: vLastTsByArbre.has(vEntry.id),
				lastCaptureTs: vLastTsByArbre.get(vEntry.id) ?? 0,
				photoFilename: vPhotoByArbre.get(vEntry.id) ?? null,
				arr: arrFor(vEntry.id),
				adresse: attrsFor(vEntry.id)?.adresse ?? null,
			})
		}

		const vCapturedCount = vRows.filter((inR) => inR.captured).length
		return { rows: vRows, capturedCount: vCapturedCount, totalCount: vRows.length, loadError: null }
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		return { rows: [], capturedCount: 0, totalCount: 0, loadError: vMsg }
	}
}

function renderActiveTab(inHost: HTMLElement, inModel: ViewModel, inTab: RemarquablesTab): void
{
	revokeObjectUrls()
	if (inTab === 'historique') renderHistorique(inHost, inModel)
	else renderCatalogue(inHost, inModel)
}

/*
Ordre des sections — parité Android ArrKey.sortKey() : 1er→20e, Bois de
Vincennes (21), Bois de Boulogne (22), Hors Paris (23). null en dernier.
*/
function arrSortKey(inKey: ArrKey | null): number
{
	if (!inKey) return 99
	switch (inKey.kind)
	{
	case 'paris': return inKey.num
	case 'vincennes': return 21
	case 'boulogne': return 22
	case 'other': return 23
	}
}

function renderCatalogue(inHost: HTMLElement, inModel: ViewModel): void
{
	// Group-by arrondissement + tri intra-section genre/espèce/id —
	// parité Android CatalogueView (« chasser arr par arr »).
	const vSections = new Map<number, { key: ArrKey | null; rows: CardRow[] }>()
	for (const vRow of inModel.rows)
	{
		const vSortKey = arrSortKey(vRow.arr)
		const vSection = vSections.get(vSortKey) ?? { key: vRow.arr, rows: [] }
		vSection.rows.push(vRow)
		vSections.set(vSortKey, vSection)
	}
	const vOrdered = [...vSections.entries()].sort((inA, inB) => inA[0] - inB[0])

	const vHtml = vOrdered.map(([, inSection]) => {
		inSection.rows.sort((inA, inB) => {
			const vGa = (inA.speciesName?.genre ?? '').toLowerCase()
			const vGb = (inB.speciesName?.genre ?? '').toLowerCase()
			if (vGa !== vGb) return vGa.localeCompare(vGb, 'fr')
			const vEa = (inA.speciesName?.espece ?? '').toLowerCase()
			const vEb = (inB.speciesName?.espece ?? '').toLowerCase()
			if (vEa !== vEb) return vEa.localeCompare(vEb, 'fr')
			return inA.arbreId - inB.arbreId
		})
		const vCaptured = inSection.rows.filter((inR) => inR.captured).length
		const vLabel = inSection.key ? arrKeyHeaderLabel(inSection.key) : 'Hors Paris'
		return `
			<section class="rmq-arr-section">
				<div class="rmq-arr-header">
					<span class="rmq-arr-header-label display">${escapeHtml(vLabel)}</span>
					<span class="rmq-arr-header-count">${vCaptured} / ${inSection.rows.length}</span>
				</div>
				${inSection.rows.map((inR) => renderCard(inR, false)).join('')}
			</section>
		`
	}).join('')

	inHost.innerHTML = `<div class="arboretum-list rmq-catalogue" role="list">${vHtml}</div>`
	wireCardClicks(inHost)
	void hydrateThumbnails(inHost, inModel.rows)
}

function renderHistorique(inHost: HTMLElement, inModel: ViewModel): void
{
	const vCaptured = inModel.rows.filter((inR) => inR.captured)
	vCaptured.sort((inA, inB) => inB.lastCaptureTs - inA.lastCaptureTs)

	if (vCaptured.length === 0)
	{
		// Libellés Android RemarquablesEmptyState ; pas de CTA (parité
		// stricte, arbitrage 2026-06-10).
		inHost.innerHTML = renderEmptyState({
			variant: 'remarquables',
			title: 'Aucun remarquable capturé.',
			body: "Pars à la chasse : la loupe en bas-gauche de la carte t'indique la distance au plus proche.",
		})
		return
	}

	inHost.innerHTML = `
		<div class="arboretum-list" role="list">
			<h2 class="arboretum-section-title">Découverts (${vCaptured.length}/${inModel.totalCount})</h2>
			${vCaptured.map((inR) => renderCard(inR, true)).join('')}
		</div>`
	wireCardClicks(inHost)
	void hydrateThumbnails(inHost, vCaptured)
}

function wireCardClicks(inHost: HTMLElement): void
{
	inHost.querySelectorAll<HTMLElement>('.arboretum-card[data-arbre-id]').forEach((inCard) => {
		inCard.addEventListener('click', () => {
			const vId = Number(inCard.dataset.arbreId)
			if (Number.isFinite(vId)) navigate(kPaths.remarquable(vId))
		})
	})
}

/* Ligne « Remarquable » orange avec le badge bichrome — parité Android. */
function renderRemarquableLine(): string
{
	return `
		<div class="rmq-card-line">
			<svg class="rmq-card-line-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10.5" fill="#FB8C00"/><path fill="#F5F1E6" d="M10.99 17.92 L10.99 13.87 C9.30 13.87 7.95 13.19 7.27 11.84 C6.60 11.50 6.60 10.15 6.93 9.13 C6.60 7.79 7.61 6.44 8.96 6.44 C9.30 5.42 10.65 4.75 12 4.75 C13.35 4.75 14.70 5.42 15.04 6.44 C16.39 6.44 17.40 7.79 17.07 9.13 C17.40 10.15 17.40 11.50 16.73 11.84 C16.05 13.19 14.70 13.87 13.01 13.87 L13.01 17.92 Z"/></svg>
			<span class="rmq-card-line-label">Remarquable</span>
		</div>
	`
}

/*
Card — parité Android DiscoveredCard / LockedRemarquableCard : thumbnail
72 px à gauche (photo ou « ? »), identité, ligne « Remarquable » orange,
arr court (substitut d'adresse, dette A9), date de capture en Historique
seulement (Android passe captureTs = null en Catalogue).
*/
function renderCard(inRow: CardRow, inShowCaptureDate: boolean): string
{
	if (!inRow.captured)
	{
		return `
			<div class="arboretum-card rmq-card locked-rmq-card" role="listitem" aria-disabled="true">
				<div class="arboretum-card-thumb rmq-card-thumb-locked" aria-hidden="true">?</div>
				<div class="arboretum-card-body">
					<div class="arboretum-card-title rmq-card-title-locked">???</div>
					${renderRemarquableLine()}
				</div>
			</div>
		`
	}

	// Cascade nv → nc → latin (parity Android v1.1.0 displayNomCommun).
	const vDisplay = inRow.speciesName ? displayName(inRow.speciesName) : 'Espèce inconnue'
	const vLatin = inRow.speciesName ? formatLatin(inRow.speciesName) : ''
	const vSubtitle = vLatin && vLatin !== vDisplay ? vLatin : null
	// Adresse complète (parité DiscoveredCard Android) ; fallback arr court.
	const vAddressLine = inRow.adresse ?? (inRow.arr && inRow.arr.kind !== 'other' ? arrKeyLabel(inRow.arr) : null)
	const vDate = inShowCaptureDate && inRow.lastCaptureTs > 0
		? `<div class="arboretum-card-foot">Capture : ${kFrenchDateShort.format(new Date(inRow.lastCaptureTs))}</div>`
		: ''

	return `
		<button class="arboretum-card rmq-card" role="listitem" data-arbre-id="${inRow.arbreId}" data-captured="true">
			<div class="arboretum-card-thumb" data-photo="${escapeAttr(inRow.photoFilename ?? '')}" aria-hidden="true"></div>
			<div class="arboretum-card-body">
				<div class="arboretum-card-title">${escapeHtml(vDisplay)}</div>
				${vSubtitle ? `<div class="arboretum-card-subtitle"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				${renderRemarquableLine()}
				${vAddressLine ? `<div class="arboretum-card-meta">${escapeHtml(vAddressLine)}</div>` : ''}
				${vDate}
			</div>
		</button>
	`
}

/*
Async-load les thumbnails photo des cards découvertes. Object URLs
trackées en module scope, révoquées au remount / changement d'onglet.
*/
async function hydrateThumbnails(inHost: HTMLElement, inRows: CardRow[]): Promise<void>
{
	for (const vRow of inRows)
	{
		if (!vRow.captured || !vRow.photoFilename) continue
		const vThumb = inHost.querySelector<HTMLElement>(
			`.arboretum-card[data-arbre-id="${vRow.arbreId}"] .arboretum-card-thumb`,
		)
		if (!vThumb) continue
		const vBlob = await loadPhoto(vRow.photoFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		vThumb.style.backgroundImage = `url("${vUrl}")`
	}
}

function formatLatin(inName: SpeciesName): string
{
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
